/**
 * Adapted from PlayCanvas Engine's GSplat asset-loader queue ownership at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */
import { getLoggerManager } from '@/shared/logging'
import type { Disposable } from '@/shared/types/core'
import { GSplatAssetLoaderBase } from './GSplatAssetLoaderBase'

const log = getLoggerManager().getLogger('gsplat:asset-loader')

export type GSplatAssetLoadState = 'idle' | 'queued' | 'loading' | 'loaded' | 'failed'

export interface GSplatAssetLoaderOptions<TResource extends Disposable> {
  readonly loadResource: (
    url: string,
    signal: AbortSignal,
    attempt: number
  ) => Promise<TResource | undefined>
  readonly maxConcurrentLoads?: number
  readonly maxRetries?: number
  /** 对应上游 registry 中 `gsplat` handler 是否仍可用。 */
  readonly canLoad?: () => boolean
  readonly onResourceReady?: (url: string, resource: TResource) => void
  readonly onResourceFailed?: (url: string, error: Error) => void
}

interface LoadEntry<TResource extends Disposable> {
  readonly id: number
  readonly generation: number
  readonly url: string
  state: GSplatAssetLoadState
  resource: TResource | null
  error: Error | null
  retryCount: number
  attempt: LoadAttempt<TResource> | null
}

interface LoadAttempt<TResource extends Disposable> {
  readonly id: number
  readonly generation: number
  readonly ownerGeneration: number
  readonly number: number
  readonly entry: LoadEntry<TResource>
  readonly controller: AbortController
}

/** URL 去重、FIFO、attempt identity 与 late-completion guard 的具体 Unified loader。 */
export class GSplatAssetLoader<
  TResource extends Disposable,
> extends GSplatAssetLoaderBase<TResource> {
  readonly maxConcurrentLoads: number
  readonly maxRetries: number

  private readonly loadResource: GSplatAssetLoaderOptions<TResource>['loadResource']
  private readonly canLoadCallback: () => boolean
  private readonly onResourceReady?: GSplatAssetLoaderOptions<TResource>['onResourceReady']
  private readonly onResourceFailed?: GSplatAssetLoaderOptions<TResource>['onResourceFailed']
  private readonly entries = new Map<string, LoadEntry<TResource>>()
  private readonly queue: LoadEntry<TResource>[] = []
  private readonly activeAttempts = new Set<LoadAttempt<TResource>>()
  private nextEntryId = 1
  private nextAttemptId = 1
  private nextGeneration = 1
  private ownerGeneration = 1
  private destroyed = false

  constructor(options: GSplatAssetLoaderOptions<TResource>) {
    super()
    this.maxConcurrentLoads = this.validateCount(
      options.maxConcurrentLoads ?? 2,
      'maxConcurrentLoads',
      1
    )
    this.maxRetries = this.validateCount(options.maxRetries ?? 2, 'maxRetries', 0)
    this.loadResource = options.loadResource
    this.canLoadCallback = options.canLoad ?? (() => true)
    this.onResourceReady = options.onResourceReady
    this.onResourceFailed = options.onResourceFailed
  }

  override load(url: string): void {
    this.assertUrl(url)
    if (this.destroyed) return
    const current = this.entries.get(url)
    if (current) {
      if (current.state === 'idle') {
        current.state = 'queued'
        current.error = null
        current.retryCount = 0
        this.queue.push(current)
      }
      this.pump()
      return
    }
    const entry: LoadEntry<TResource> = {
      id: this.nextEntryId++,
      generation: this.nextGeneration++,
      url,
      state: 'queued',
      resource: null,
      error: null,
      retryCount: 0,
      attempt: null,
    }
    this.entries.set(url, entry)
    this.queue.push(entry)
    this.pump()
  }

  override unload(url: string): void {
    const entry = this.entries.get(url)
    if (!entry) return
    this.entries.delete(url)
    entry.attempt?.controller.abort()
    entry.resource?.dispose()
    entry.resource = null
    this.pump()
  }

  override getResource(url: string): TResource | undefined {
    const entry = this.entries.get(url)
    return entry?.state === 'loaded' ? (entry.resource ?? undefined) : undefined
  }

  override hasFailed(url: string): boolean {
    return this.entries.get(url)?.state === 'failed'
  }

  getState(url: string): GSplatAssetLoadState | undefined {
    return this.entries.get(url)?.state
  }

  get activeLoadCount(): number {
    return this.activeAttempts.size
  }

  get queuedLoadCount(): number {
    let count = 0
    for (const entry of this.queue) {
      if (this.isLive(entry) && entry.state === 'queued') count++
    }
    return count
  }

  override destroy(): void {
    if (this.destroyed) return
    this.destroyed = true
    this.ownerGeneration++
    for (const attempt of this.activeAttempts) attempt.controller.abort()
    for (const entry of this.entries.values()) {
      entry.resource?.dispose()
      entry.resource = null
    }
    this.entries.clear()
    this.queue.length = 0
  }

  /** handler 被重新注册后由宿主显式唤醒队列；重复调用安全。 */
  notifyAvailabilityChanged(): void {
    this.pump()
  }

  private pump(): void {
    if (this.destroyed || !this.canLoadCallback()) return
    while (this.activeAttempts.size < this.maxConcurrentLoads && this.queue.length > 0) {
      const entry = this.queue.shift()
      if (!entry || !this.isLive(entry) || entry.state !== 'queued') continue
      this.startAttempt(entry)
    }
  }

  private startAttempt(entry: LoadEntry<TResource>): void {
    const attempt: LoadAttempt<TResource> = {
      id: this.nextAttemptId++,
      generation: entry.generation,
      ownerGeneration: this.ownerGeneration,
      number: entry.retryCount + 1,
      entry,
      controller: new AbortController(),
    }
    entry.state = 'loading'
    entry.attempt = attempt
    this.activeAttempts.add(attempt)
    void this.runAttempt(attempt)
  }

  private async runAttempt(attempt: LoadAttempt<TResource>): Promise<void> {
    const { entry } = attempt
    try {
      const resource = await this.loadResource(entry.url, attempt.controller.signal, attempt.number)
      if (resource === undefined) {
        if (this.isCurrentAttempt(attempt)) {
          // 与上游 loaded-without-resource 一致：取消不形成永久失败，后续 load 可重新尝试。
          entry.resource = null
          entry.error = null
          entry.state = 'idle'
        }
        return
      }
      if (!this.isCurrentAttempt(attempt)) {
        resource.dispose()
        return
      }
      entry.resource = resource
      entry.error = null
      entry.state = 'loaded'
      this.notifyReady(entry.url, resource)
    } catch (cause) {
      if (!this.isCurrentAttempt(attempt)) return
      if (attempt.controller.signal.aborted) {
        entry.state = 'idle'
        entry.error = null
        return
      }
      const error = cause instanceof Error ? cause : new Error(String(cause))
      entry.error = error
      if (!this.canLoadCallback()) {
        // registry 销毁/handler 暂时移除不消耗真实错误 retry；恢复后继续同一逻辑 entry。
        entry.state = 'queued'
        this.queue.push(entry)
      } else if (entry.retryCount < this.maxRetries) {
        entry.retryCount++
        entry.state = 'queued'
        this.queue.push(entry)
      } else {
        entry.state = 'failed'
        this.notifyFailed(entry.url, error)
      }
    } finally {
      this.activeAttempts.delete(attempt)
      if (entry.attempt === attempt) entry.attempt = null
      this.pump()
    }
  }

  private notifyReady(url: string, resource: TResource): void {
    try {
      this.onResourceReady?.(url, resource)
    } catch (error) {
      log.error(
        `GSplatAssetLoader onResourceReady 失败：url=${url}, reason=${error instanceof Error ? error.message : String(error)}`
      )
    }
  }

  private notifyFailed(url: string, error: Error): void {
    try {
      this.onResourceFailed?.(url, error)
    } catch (callbackError) {
      log.error(
        `GSplatAssetLoader onResourceFailed 失败：url=${url}, reason=${callbackError instanceof Error ? callbackError.message : String(callbackError)}`
      )
    }
  }

  private isLive(entry: LoadEntry<TResource>): boolean {
    return !this.destroyed && this.entries.get(entry.url) === entry
  }

  private isCurrentAttempt(attempt: LoadAttempt<TResource>): boolean {
    return (
      attempt.ownerGeneration === this.ownerGeneration &&
      attempt.generation === attempt.entry.generation &&
      this.isLive(attempt.entry) &&
      attempt.entry.attempt === attempt
    )
  }

  private assertUrl(url: string): void {
    if (!url) throw new Error('GSplatAssetLoader URL 不能为空')
  }

  private validateCount(value: number, label: string, minimum: number): number {
    if (!Number.isSafeInteger(value) || value < minimum) {
      throw new Error(`非法 GSplatAssetLoader ${label}: ${value}`)
    }
    return value
  }
}
